/**
 * AURA v2 AppShell — rewritten Apr 22, 2026 (Phase 2 UI rebuild).
 *
 * Layout:
 *   ┌─── TopBar ─────────────────────────────────────────┐
 *   │ Sidebar │        main (routed)           │ AIPanel │
 *   └─────────┴────────────────────────────────┴─────────┘
 *   CommandPalette is rendered at the root (fixed overlay).
 *
 * No backend changes. Every child route continues to resolve as before.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { Sidebar, type SidebarView } from './Sidebar';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { AIPanel } from './AIPanel';
import { useAuthStore } from '../../stores/auth';
import { resolveRoleKey } from '../../pages/dashboard/roleLayouts';
import { useIdleDetector } from '../../hooks/useIdleDetector';
import { useMediaQuery } from '../../hooks/useMediaQuery';

type Density = 'compact' | 'comfortable';

const DENSITY_KEY = 'aura-density';
const VIEW_OVERRIDE_KEY = 'aura-view-override';

export function AppShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Desktop: sidebar collapsed/expanded. Mobile: drawer open/closed (default closed).
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Mobile drawer open state — separate from desktop collapsed state so
  // switching viewport doesn't leave desktop sidebar in a bad state.
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Close mobile drawer on route change (user navigated to a page).
  useEffect(() => {
    if (isMobile) setMobileDrawerOpen(false);
  }, [location.pathname, isMobile]);

  const [cmdOpen, setCmdOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Density — persisted in localStorage
  const [density, setDensity] = useState<Density>(() => {
    if (typeof window === 'undefined') return 'comfortable';
    const saved = window.localStorage.getItem(DENSITY_KEY) as Density | null;
    return saved === 'compact' ? 'compact' : 'comfortable';
  });
  useEffect(() => {
    window.localStorage.setItem(DENSITY_KEY, density);
  }, [density]);

  // Admin-only dept view override — sessionStorage so it resets on browser close
  const [viewOverride, setViewOverrideState] = useState<SidebarView | null>(() => {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(VIEW_OVERRIDE_KEY);
    return (raw as SidebarView | null) ?? null;
  });
  const setViewOverride = (v: SidebarView | null) => {
    setViewOverrideState(v);
    if (v) window.sessionStorage.setItem(VIEW_OVERRIDE_KEY, v);
    else window.sessionStorage.removeItem(VIEW_OVERRIDE_KEY);
    // BUG-03 fix: notify DashboardPage (and any other subscriber) that the
    // view-override changed so they re-render without needing a manual refresh.
    window.dispatchEvent(new CustomEvent('aura:view-override:change', { detail: v }));
  };

  const selectViewOverride = (view: SidebarView | null) => {
    setViewOverride(view);
    if (view === 'purchase' || view === 'veda_purchase_head') {
      navigate('/veda-agro/cockpit');
    }
  };

  // Global ⌘K / ⌘J keyboard shortcuts
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod) return;
      const key = e.key.toLowerCase();
      if (key === 'k') {
        e.preventDefault();
        setCmdOpen((o) => !o);
      } else if (key === 'j') {
        e.preventDefault();
        setAiOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  // Apr 27, 2026 — global event hooks so dashboard widgets / page buttons
  // can open the AI panel or command palette without prop-drilling. Pages
  // dispatch CustomEvent('aura-ai-open') / 'aura-cmd-open'. Optional detail.preset
  // is forwarded to AIPanel via sessionStorage for the panel to seed its prompt.
  useEffect(() => {
    const onAiOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'object') {
        try { window.sessionStorage.setItem('aura-ai-preset', JSON.stringify(detail)); } catch {}
      }
      setAiOpen(true);
    };
    const onCmdOpen = () => setCmdOpen(true);
    window.addEventListener('aura-ai-open', onAiOpen as EventListener);
    window.addEventListener('aura-cmd-open', onCmdOpen);
    return () => {
      window.removeEventListener('aura-ai-open', onAiOpen as EventListener);
      window.removeEventListener('aura-cmd-open', onCmdOpen);
    };
  }, []);

  // Resolve the current user's dashboard role — fed into the AI panel so
  // suggestions are tailored (e.g. Production Head ≠ Owner).
  const { user } = useAuthStore();

  // 2026-06-18 fix: the admin-only "View as" override is kept in sessionStorage,
  // which SURVIVES page reloads. If it isn't cleared when the logged-in user
  // changes (login / logout / user-switch within the same tab), a leftover
  // override leaks into the next user's session and overrides their sidebar —
  // e.g. a stale 'production' preview blanked the Purchase Manager's nav (every
  // Production item is gated on the `manufacturing` module, which Veda doesn't
  // enable). Reset it whenever the user identity changes so a role-preview can
  // never bleed across users or reloads.
  useEffect(() => {
    setViewOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Squad G — Idle detection + attendance tracking.
  // Threshold sourced from tenant config; default 3 min until loaded.
  // Fetches activityInactivityMinutes from the admin tenant-config endpoint.
  const [idleThreshold, setIdleThreshold] = useState(3);
  useEffect(() => {
    if (!user) return;
    fetch('/api/v1/admin/tenant-config', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const mins = data?.data?.activityInactivityMinutes;
        if (typeof mins === 'number' && mins > 0) setIdleThreshold(mins);
      })
      .catch(() => {/* best-effort */});
  }, [user?.id]);

  useIdleDetector({ thresholdMinutes: idleThreshold, enabled: !!user });
  const role = useMemo(() => resolveRoleKey(user as never), [user]);

  return (
    <ThemeProvider>
      <div
        className="aura-root flex h-screen flex-col"
        data-density={density}
        style={{ background: 'var(--ivory-0)', color: 'var(--ink-1)' }}
      >
        {/* TopBar spans full width */}
        <div data-aura-chrome="topbar">
          <TopBar
            onCmdOpen={() => setCmdOpen(true)}
            onAiOpen={() => setAiOpen((o) => !o)}
            density={density}
            onDensityChange={setDensity}
            viewOverride={viewOverride}
            onViewOverrideChange={selectViewOverride}
            isMobile={isMobile}
            onHamburger={() => setMobileDrawerOpen((o) => !o)}
          />
        </div>

        {/* Row: sidebar · main · AI panel */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* --- Desktop sidebar (hidden on mobile) --- */}
          {!isMobile && (
            <div data-aura-chrome="sidebar" className="h-full min-h-0">
              <Sidebar
                open={sidebarOpen}
                onToggle={() => setSidebarOpen((o) => !o)}
                viewOverride={viewOverride}
              />
            </div>
          )}

          {/* --- Mobile drawer overlay --- */}
          {isMobile && mobileDrawerOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                style={{ background: 'rgba(0,0,0,0.35)' }}
                onClick={() => setMobileDrawerOpen(false)}
                aria-hidden="true"
              />
              {/* Drawer */}
              <div
                className="fixed inset-y-0 left-0 z-50 flex flex-col"
                style={{ width: 240 }}
              >
                <Sidebar
                  open
                  onToggle={() => setMobileDrawerOpen(false)}
                  viewOverride={viewOverride}
                  onMobileClose={() => setMobileDrawerOpen(false)}
                />
              </div>
            </>
          )}

          {/* Apr 23, 2026 (Bug 3) — min-w-0 so flex child can actually shrink.
              Without it, wide tables / grids inside children forced the main
              column wider than its flex allotment and pushed action buttons
              off-screen when the AI panel was open. */}
          <main
            className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden p-6"
            data-aura-chrome="main"
          >
            {children}
          </main>
          <div data-aura-chrome="ai-panel">
            <AIPanel
              open={aiOpen}
              onClose={() => setAiOpen(false)}
              role={role}
              pathname={location.pathname}
            />
          </div>
        </div>

        {/* Global overlays */}
        <CommandPalette
          open={cmdOpen}
          onClose={() => setCmdOpen(false)}
          onAiOpen={() => setAiOpen(true)}
        />
      </div>
    </ThemeProvider>
  );
}
